import { conditionallyAddPlus, filterQuote } from 'utils/selfServeHelpers';

import {
  CreateAndChargeNewOrgCardInput,
  QuoteInput,
  LeadInput,
  SelfServeHelpInput,
} from 'globalTypes';
import CREATE_AND_CHARGE_NEW_ORG_CARD_MUTATION from 'admin/mutations/CreateAndChargeNewOrgCard.gql';
import CREATE_SELF_SERVE_HELP_REQUEST_MUTATION from 'admin/mutations/CreateSelfServeHelpRequest.gql';
import CREATE_SELF_SERVE_QUOTE_MUTATION from 'admin/mutations/CreateSelfServeQuote.gql';
import UPDATE_SELF_SERVE_QUOTE_MUTATION from 'admin/mutations/UpdateSelfServeQuote.gql';
import CREATE_SELF_SERVE_LEAD_MUTATION from 'admin/mutations/CreateSelfServeLead.gql';
import {
  UpgradePlanQuote,
  UpgradePlanData,
} from 'admin/components/AdminUpgradePlan/AdminUpgradePlanContainer';
import RemindAPI from 'lib/RemindAPI';

import {
  CreateAndChargeNewOrgCard,
  CreateAndChargeNewOrgCardVariables,
} from 'admin/mutations/__generated__/CreateAndChargeNewOrgCard';

import {
  SelfServeRequestHelpMutation,
  SelfServeRequestHelpMutationVariables,
} from 'admin/mutations/__generated__/SelfServeRequestHelpMutation';

import { CreateQuote, CreateQuoteVariables } from 'admin/mutations/__generated__/CreateQuote';
import { CreateLead, CreateLeadVariables } from 'admin/mutations/__generated__/CreateLead';

import { UpdateQuote, UpdateQuoteVariables } from 'admin/mutations/__generated__/UpdateQuote';

type SuccessResponse<T> = { success: true } & T;
type ErrorResponse<E> = { success: false } & E;
export type ActionResponse<T, E = { errorMessage: string }> = SuccessResponse<T> | ErrorResponse<E>;
export type QuotePayload = { quote: UpgradePlanQuote };

export function logSelfServeError(
  action: String,
  consoleErrorText: String,
  organizationUuid?: String
) {
  // Rollbar uses the 'AdminSelfServe' to determine if it should notify errors to #self-serve-errors
  // channel in slack.  If you change this tag, change it there too.
  const printableOrgId = organizationUuid ?? 'unknown';
  console.error(
    `AdminSelfServe (${action}) - organization='${printableOrgId}' error='${consoleErrorText}'`
  );
}

export async function createSelfServeHelpRequest(
  input: SelfServeHelpInput
): Promise<ActionResponse<{}>> {
  const customerFacingErrorMessage =
    'There was a problem submitting your request. Please try again';

  try {
    const { sendSelfServeHelpRequest: resp } = await RemindAPI.execute<
      SelfServeRequestHelpMutation,
      SelfServeRequestHelpMutationVariables
    >({
      mutation: CREATE_SELF_SERVE_HELP_REQUEST_MUTATION,
      variables: { input },
    });

    if (resp.error) {
      const consoleLogError =
        resp.error.fieldErrors?.map(fe => fe.message).join(', ') || (resp.error.message ?? '');
      logSelfServeError(
        'createSelfServeHelpRequest',
        consoleLogError,
        input.organizationUuid || ''
      );
      return {
        success: false,
        errorMessage: customerFacingErrorMessage,
      };
    }
    return { success: true };
  } catch (e) {
    logSelfServeError(
      'createSelfServeHelpRequest',
      `Exception on GQL mutation: ${JSON.stringify(e)}`,
      ''
    );
    return { success: false, errorMessage: customerFacingErrorMessage };
  }
}

export async function createSelfServeLeadWithoutQuote(
  input: LeadInput
): Promise<ActionResponse<{}>> {
  const customerFacingErrorMessage = 'There was a problem submitting your data. Please try again';
  try {
    const { createLead: resp } = await RemindAPI.execute<CreateLead, CreateLeadVariables>({
      mutation: CREATE_SELF_SERVE_LEAD_MUTATION,
      variables: { input },
    });

    if (resp.error) {
      const consoleLogError =
        resp.error.fieldErrors?.map(fe => fe.message).join(', ') || (resp.error.message ?? '');
      logSelfServeError('createSelfServeLeadWithoutQuote', consoleLogError);
      return {
        success: false,
        errorMessage: customerFacingErrorMessage,
      };
    }
    return { success: true };
  } catch (e) {
    logSelfServeError(
      'createSelfServeLeadWithoutQuote',
      `Exception on GQL mutation: ${JSON.stringify(e)}`
    );
    return { success: false, errorMessage: customerFacingErrorMessage };
  }
}

export async function createSelfServeQuote(
  input: QuoteInput
): Promise<ActionResponse<QuotePayload>> {
  const customerFacingErrorMessage =
    'There was a problem submitting your request for a quote. Please try again';
  try {
    const { createQuote: resp } = await RemindAPI.execute<CreateQuote, CreateQuoteVariables>({
      mutation: CREATE_SELF_SERVE_QUOTE_MUTATION,
      variables: { input },
    });

    if (resp.error) {
      const consoleLogError =
        resp.error.fieldErrors?.map(fe => fe.message).join(', ') || (resp.error.message ?? '');
      logSelfServeError('createSelfServeQuote', consoleLogError, input.organizationUuid);
      return { success: false, errorMessage: customerFacingErrorMessage };
    }

    if (!resp.quote) {
      logSelfServeError(
        'createSelfServeQuote',
        'Unknown error - no quote in GQL response',
        input.organizationUuid
      );
      return { success: false, errorMessage: customerFacingErrorMessage };
    }

    return { success: true, quote: filterQuote(resp.quote) };
  } catch (e) {
    logSelfServeError(
      'createSelfServeQuote',
      `Exception on GQL mutation: ${JSON.stringify(e)}`,
      input.organizationUuid
    );
    return { success: false, errorMessage: customerFacingErrorMessage };
  }
}

export function filterQuoteUpdates(quote: UpgradePlanQuote, plan: UpgradePlanData): QuoteInput {
  return {
    organizationUuid: quote.organization.uuid,
    studentsCount: quote.studentsCount,
    teachersCount: quote.teachersCount,
    startDate: quote.startDate,
    organizationAddress: quote.organizationAddress,
    billingFirstName: plan.billingFirstName ?? plan.requesterFirstName,
    billingLastName: plan.billingLastName ?? plan.requesterLastName,
    billingEmail: plan.billingEmail ?? plan.requesterEmail,
    billingPhoneNumber: conditionallyAddPlus(plan.requesterPhone || ''),
    requesterFirstName: plan.requesterFirstName ?? plan.billingFirstName,
    requesterLastName: plan.requesterLastName ?? plan.billingLastName,
    requesterEmail: plan.requesterEmail ?? plan.billingEmail,
    requesterPhoneNumber: conditionallyAddPlus(plan.requesterPhone || ''),
    taxExempt: plan.taxExempt,
  };
}

export async function updateQuote(
  quoteUuid: string,
  input: QuoteInput
): Promise<ActionResponse<QuotePayload>> {
  const customerFacingErrorMessage = 'There was a problem updating your quote. Please try again';

  try {
    const { updateQuote: resp } = await RemindAPI.execute<UpdateQuote, UpdateQuoteVariables>({
      mutation: UPDATE_SELF_SERVE_QUOTE_MUTATION,
      variables: { uuid: quoteUuid, input },
    });

    if (resp.error) {
      const consoleLogError =
        resp.error.fieldErrors?.map(fe => fe.message).join(', ') || (resp.error.message ?? '');
      logSelfServeError('updateQuote', consoleLogError, input.organizationUuid);
      return { success: false, errorMessage: customerFacingErrorMessage };
    }

    if (!resp.quote) {
      logSelfServeError(
        'updateQuote',
        'Unknown error - no quote in GQL response',
        input.organizationUuid
      );
      return { success: false, errorMessage: customerFacingErrorMessage };
    }

    return { success: true, quote: filterQuote(resp.quote) };
  } catch (e) {
    logSelfServeError(
      'updateQuote',
      `Exception on GQL mutation: ${JSON.stringify(e)}`,
      input.organizationUuid
    );
    return { success: false, errorMessage: customerFacingErrorMessage };
  }
}

export function convertSelfServeQuote(
  sourceId: string,
  organizationUuid: string,
  organizationId: number,
  price: number,
  quoteUuid: string
) {
  const mutationInput = {
    orgUuid: organizationUuid,
    orgId: organizationId,
    stripeSourceId: sourceId,
    amount: price,
    quoteUuid,
  } as CreateAndChargeNewOrgCardInput;
  const errorText = `Could not charge card for self serve quote quote=${quoteUuid} `;

  return RemindAPI.execute<CreateAndChargeNewOrgCard, CreateAndChargeNewOrgCardVariables>({
    mutation: CREATE_AND_CHARGE_NEW_ORG_CARD_MUTATION,
    variables: { input: mutationInput },
  })
    .then(({ createAndChargeNewOrgCard: resp }) => {
      if (resp.error) {
        logSelfServeError(
          'convertSelfServeQuote',
          organizationUuid,
          errorText + resp.error.message
        );
        return { success: false, errorMessage: resp.error.message };
      } else if (resp.declineReason) {
        console.warn(resp.declineReason);
        logSelfServeError(
          'convertSelfServeQuote',
          errorText + `card declined: ${resp.declineReason}`,
          organizationUuid
        );
        return { success: false, declineReason: resp.declineReason };
      } else {
        return { success: true };
      }
    })
    .catch(err => {
      console.warn(err);
      logSelfServeError(
        'convertSelfServeQuote',
        errorText + `Exception on GQL mutation: ${JSON.stringify(err)}`,
        organizationUuid
      );
      return { success: false, declineReason: null, errorMessage: err.message };
    });
}
